import ko from "date-fns/locale/ko";
import React, {
  useEffect,
  useMemo,
  useState
} from "react";
import Select from "react-select";
import moment from "moment";
import styled from "styled-components";

import {
  CustomDatePicker
} from "../../assets/GlobalStyles";
import {
  getLastDay,
  getLastMonth,
  getLastNinetyDay,
  getLastThirtyDay,
  getLastWeekDay,
  getThisMonth,
  getToDay
} from "../../common/DateUtils";
import {ReportCalendarContainer} from "../../pages/reports/ReportSharedLayout";
import Checkbox from "../common/Checkbox";

const DEVICE_TYPES = [
  {
    id: "PC",
    value: "PC",
    label: "PC"
  },
  {
    id: "MOBILE",
    value: "MOBILE",
    label: "모바일 웹"
  },
  {
    id: "RESPONSIVE_WEB",
    value: "RESPONSIVE_WEB",
    label: "반응형 웹"
  },
  {
    id: "APP",
    value: "APP",
    label: "APP"
  }
];

const dashboardSelectStyle = {
  control: (
      provided,
      state
  ) => ({
    ...provided,
    minHeight: 40,
    height: 40,
    borderColor: state.isFocused
        ? "#f5811f"
        : "#e5e5e5",
    borderRadius: 6,
    backgroundColor: "#fff",
    boxShadow: "none",
    fontSize: 13,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#f1d4c2"
    }
  }),
  valueContainer: provided => ({
    ...provided,
    height: 40,
    padding: "0 11px"
  }),
  input: provided => ({
    ...provided,
    margin: 0,
    padding: 0
  }),
  indicatorsContainer:
      provided => ({
        ...provided,
        height: 40
      }),
  indicatorSeparator: () => ({
    display: "none"
  }),
  dropdownIndicator: provided => ({
    ...provided,
    padding: "0 9px",
    color: "#888"
  }),
  singleValue: provided => ({
    ...provided,
    color: "#444"
  }),
  menu: provided => ({
    ...provided,
    zIndex: 20,
    overflow: "hidden",
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    boxShadow:
        "0 8px 24px rgba(0, 0, 0, 0.1)"
  }),
  option: (
      provided,
      state
  ) => ({
    ...provided,
    padding: "9px 11px",
    backgroundColor: state.isSelected
        ? "#fff4e8"
        : state.isFocused
            ? "#fffaf6"
            : "#fff",
    color: state.isSelected
        ? "#f5811f"
        : "#555",
    fontSize: 13,
    cursor: "pointer"
  })
};

export function DashBoardCondition({
  handleData,
  productType,
  targetingType,
  searchState,
  setSearchState,
  defaultOpen = false
}) {
  const [dateRange, setDateRange] =
      useState([
        new Date(
            getThisMonth().startDay
        ),
        new Date(getToDay())
      ]);

  const [startDate, endDate] =
      dateRange;

  const [dateActive, setDateActive] =
      useState("thisMonth");

  const [
    isDeviceCheckedAll,
    setIsDeviceCheckedAll
  ] = useState(true);

  const [isOpen, setIsOpen] =
      useState(defaultOpen);

  const selectedDeviceTypes =
      useMemo(
          () =>
              Array.isArray(
                  searchState?.deviceTypes
              )
                  ? searchState.deviceTypes
                  : [],
          [searchState?.deviceTypes]
      );

  useEffect(() => {
    setIsDeviceCheckedAll(
        selectedDeviceTypes.length ===
        DEVICE_TYPES.length
    );
  }, [selectedDeviceTypes]);

  const updateSearchState =
      nextValues => {
        setSearchState(prev => ({
          ...(prev ?? {}),
          ...nextValues
        }));
      };

      const handleChangeDateRange =
      date => {
        setDateRange(date);

        if (!date?.[0] || !date?.[1]) {
          return;
        }

        const nextDateCondition = {
          searchStartDate: moment(
              date[0]
          ).format("YYYY-MM-DD"),
          searchEndDate: moment(
              date[1]
          ).format("YYYY-MM-DD")
        };

        updateSearchState(nextDateCondition);
        handleData(nextDateCondition);

      };

  const handleRangeDate = rangeType => {
    const ranges = {
      thisMonth: getThisMonth(),
      lastMonth: getLastMonth(),
      today: {
        startDay: getToDay(),
        endDay: getToDay()
      },
      lastDay: {
        startDay: getLastDay(),
        endDay: getLastDay()
      },
      lastWeekDay: getLastWeekDay(),
      lastThirtyDay: getLastThirtyDay(),
      lastNinetyDay: getLastNinetyDay()
    };
    const selectedRange = ranges[rangeType];

    if (!selectedRange) {
      return;
    }

    setDateActive(rangeType);
    handleChangeDateRange([
      new Date(selectedRange.startDay),
      new Date(selectedRange.endDay)
    ]);
  };

  const handleProductType =
      selectedProductType => {
        updateSearchState({
          productType:
          selectedProductType?.value
        });
      };

  const handleTargetingType =
      selectedTargeting => {
        updateSearchState({
          targetingType:
          selectedTargeting?.value
        });
      };

  const handleDeviceTypeChangeAll =
      event => {
        if (!event.target.checked) {
          return;
        }

        updateSearchState({
          deviceTypes:
              DEVICE_TYPES.map(
                  item => item.value
              )
        });

        setIsDeviceCheckedAll(true);
      };

  const handleDeviceChange =
      event => {
        const {checked, value} =
            event.currentTarget;

        if (checked) {
          updateSearchState({
            deviceTypes:
                Array.from(
                    new Set([
                      ...selectedDeviceTypes,
                      value
                    ])
                )
          });

          return;
        }

        if (
            selectedDeviceTypes.length <=
            1
        ) {
          return;
        }

        updateSearchState({
          deviceTypes:
              selectedDeviceTypes.filter(
                  item => item !== value
              )
        });
      };

  const handleSubmit = event => {
    event?.preventDefault();
    handleData();
    setIsOpen(false);
  };

  const selectedProduct =
      productType?.find(
          item =>
              item.value ===
              searchState?.productType
      ) ?? null;

  const selectedTargeting =
      targetingType?.find(
          item =>
              item.value ===
              searchState?.targetingType
      ) ?? null;

  const selectedProductLabel =
      selectedProduct?.label ??
      selectedProduct?.name ??
      "전체 상품";

  const selectedDeviceLabel =
      isDeviceCheckedAll
          ? "전체 디바이스"
          : `${selectedDeviceTypes.length}개 디바이스`;

  const selectedDateLabel =
      searchState?.searchStartDate &&
      searchState?.searchEndDate
          ? `${searchState.searchStartDate} ~ ${searchState.searchEndDate}`
          : "기간 미설정";

  if (defaultOpen) {
    return (
      <PeriodControlRow>
        <PeriodOnlyCondition>
          <PeriodOnlyLabel>조회 기간</PeriodOnlyLabel>
          <DatePickerWrap>
            <CalendarIconWrap aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24">
                <path fill="currentColor" d="M7 2h2v2h6V2h2v2h3v18H4V4h3V2Zm11 8H6v10h12V10ZM6 6v2h12V6H6Z"/>
              </svg>
            </CalendarIconWrap>
            <StyledDatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                minDate={new Date(getLastNinetyDay().startDay)}
                maxDate={new Date()}
                onChange={date => {
                  setDateActive("");
                  handleChangeDateRange(date);
                }}
                dateFormat="yyyy-MM-dd"
                locale={ko}
                isClearable={false}
                monthsShown={2}
                openToDate={startDate}
                focusSelectedMonth
                calendarContainer={props => (
                  <ReportCalendarContainer
                      {...props}
                      active={dateActive}
                      onSelect={handleRangeDate}
                  />
                )}
            />
          </DatePickerWrap>
        </PeriodOnlyCondition>
        <PeriodResetButton
            type="button"
            onClick={() => handleRangeDate("thisMonth")}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.75 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35Z"/>
          </svg>
          초기화
        </PeriodResetButton>
      </PeriodControlRow>
    );
  }

  return (
      <ConditionPanel $open={isOpen}>
        <ConditionToggleButton
            type="button"
            $open={isOpen}
            aria-expanded={isOpen}
            aria-controls="dashboard-condition-body"
            onClick={() =>
                setIsOpen(prev => !prev)
            }
        >
          <HeaderTitleArea>
            <HeaderAccent />

            <div>
              <HeaderTitle>
                조회 조건
              </HeaderTitle>

              <HeaderDescription>
                상품, 디바이스, 기간과 광고주를
                기준으로 대시보드 데이터를
                조회합니다.
              </HeaderDescription>
            </div>
          </HeaderTitleArea>

          <ConditionHeaderRight>
            {!isOpen && (
                <ConditionSummary>
                  <SummaryChip>
                    {selectedProductLabel}
                  </SummaryChip>

                  <SummaryChip>
                    {selectedDeviceLabel}
                  </SummaryChip>

                  <SummaryChip>
                    {selectedDateLabel}
                  </SummaryChip>

                </ConditionSummary>
            )}

            <DropdownIcon
                $open={isOpen}
                aria-hidden="true"
            >
              <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
              >
                <path
                    fill="currentColor"
                    d="m7 10 5 5 5-5H7Z"
                />
              </svg>
            </DropdownIcon>
          </ConditionHeaderRight>
        </ConditionToggleButton>

        {isOpen && (
            <ConditionBody
                id="dashboard-condition-body"
            >
              <FilterGrid>
                <FilterItem>
                  <FilterLabel>
                    광고 상품
                  </FilterLabel>

                  <FilterControl>
                    <Select
                        options={productType}
                        value={selectedProduct}
                        onChange={
                          handleProductType
                        }
                        styles={
                          dashboardSelectStyle
                        }
                        isSearchable={false}
                    />
                  </FilterControl>
                </FilterItem>

                {targetingType?.length >
                    0 && (
                        <FilterItem>
                          <FilterLabel>
                            타겟팅
                          </FilterLabel>

                          <FilterControl>
                            <Select
                                options={
                                  targetingType
                                }
                                value={
                                  selectedTargeting
                                }
                                onChange={
                                  handleTargetingType
                                }
                                styles={
                                  dashboardSelectStyle
                                }
                                isSearchable={false}
                            />
                          </FilterControl>
                        </FilterItem>
                    )}

                <DeviceFilterItem>
                  <FilterLabel>
                    디바이스
                  </FilterLabel>

                  <DeviceOptions>
                    <DeviceOption>
                      <Checkbox
                          label="전체"
                          type="c"
                          id="all"
                          value="All"
                          isChecked={
                            isDeviceCheckedAll
                          }
                          onChange={
                            handleDeviceTypeChangeAll
                          }
                      />
                    </DeviceOption>

                    {DEVICE_TYPES.map(
                        device => (
                            <DeviceOption
                                key={device.value}
                            >
                              <Checkbox
                                  label={
                                    device.label
                                  }
                                  type="c"
                                  id={device.id}
                                  value={
                                    device.value
                                  }
                                  isChecked={
                                    selectedDeviceTypes.includes(
                                        device.value
                                    )
                                  }
                                  onChange={
                                    handleDeviceChange
                                  }
                              />
                            </DeviceOption>
                        )
                    )}
                  </DeviceOptions>
                </DeviceFilterItem>

                <PeriodFilterItem>
                  <FilterLabel>조회 기간</FilterLabel>
                  <DatePickerWrap>
                    <CalendarIconWrap aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M7 2h2v2h6V2h2v2h3v18H4V4h3V2Zm11 8H6v10h12V10ZM6 6v2h12V6H6Z"/>
                      </svg>
                    </CalendarIconWrap>
                    <StyledDatePicker
                        selectsRange={true}
                        startDate={startDate}
                        endDate={endDate}
                        minDate={new Date(getLastNinetyDay().startDay)}
                        maxDate={new Date()}
                        onChange={date => {
                          setDateActive("");
                          handleChangeDateRange(date);
                        }}
                        dateFormat="yyyy-MM-dd"
                        locale={ko}
                        isClearable={false}
                        monthsShown={2}
                        openToDate={startDate}
                        focusSelectedMonth
                        calendarContainer={props => (
                          <ReportCalendarContainer
                              {...props}
                              active={dateActive}
                              onSelect={handleRangeDate}
                          />
                        )}
                    />
                  </DatePickerWrap>
                </PeriodFilterItem>

                <ApplyButton type="button" onClick={handleSubmit}>조회</ApplyButton>
              </FilterGrid>
            </ConditionBody>
        )}
      </ConditionPanel>
  );
}

const PeriodControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 620px) {
    width: 100%;
    align-items: stretch;
  }
`;

const PeriodOnlyCondition = styled.section`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: fit-content;
  margin-left: auto;
  padding: 6px 8px;
  border: 1px solid #e5e5e5;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(31, 41, 55, 0.04);

  & > div:last-of-type {
    width: 220px;
  }

  @media (max-width: 620px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const PeriodOnlyLabel = styled.div`
  padding-left: 5px;
  padding-bottom: 0;
  color: #555;
  font-size: var(--font-size-13);
  font-weight: 700;

  @media (max-width: 620px) { padding-bottom: 0; }
`;

const PeriodResetButton = styled.button`
  display: inline-flex;
  min-width: 68px;
  height: 47px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 14px;
  border: 1px solid #dfe3e7;
  border-radius: 9px;
  background: #fff;
  color: #666;
  font-size: var(--font-size-12);
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #f1d4c2;
    background: #fffaf6;
    color: #f5811f;
  }
`;

const ConditionPanel = styled.section`
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid
  ${({$open}) =>
          $open
                  ? "#f1d4c2"
                  : "#e5e5e5"};
  border-radius: 9px;
  background-color: #fff;
  transition:
          border-color 0.18s ease,
          box-shadow 0.18s ease;

  ${({$open}) =>
          $open
                  ? `
        box-shadow:
          0 4px 14px
          rgba(245, 129, 31, 0.06);
      `
                  : `
        box-shadow: none;
      `}
`;

const ConditionToggleButton = styled.button`
  display: flex;
  width: 100%;
  min-height: 66px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px 18px;
  border: 0;
  border-bottom: 1px solid
  ${({$open}) =>
          $open
                  ? "#f1e4da"
                  : "transparent"};
  background-color: #f9fafb;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: #fffaf6;
  }

  &:focus-visible {
    outline: 1px solid #f5811f;
    outline-offset: -1px;
  }

  @media (max-width: 900px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
`;

const ConditionHeaderRight = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;

  @media (max-width: 900px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const ConditionSummary = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  flex-wrap: wrap;

  @media (max-width: 700px) {
    justify-content: flex-start;
  }
`;

const SummaryChip = styled.span`
  display: inline-flex;
  max-width: 190px;
  min-height: 25px;
  align-items: center;
  padding: 0 9px;
  overflow: hidden;
  border: 1px solid
  ${({$accent}) =>
          $accent
                  ? "#f1d4c2"
                  : "#e5e5e5"};
  border-radius: 13px;
  background-color:
          ${({$accent}) =>
                  $accent
                          ? "#fff4e8"
                          : "#fff"};
  color:
          ${({$accent}) =>
                  $accent
                          ? "#f5811f"
                          : "#777"};
  font-size: var(--font-size-11);
  font-weight:
          ${({$accent}) =>
                  $accent
                          ? 700
                          : 500};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownState = styled.span`
  flex: 0 0 auto;
  color: #777;
  font-size: var(--font-size-11);
  font-weight: 600;
`;

const DropdownIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #f5811f;
  transform:
          rotate(
                  ${({$open}) =>
                          $open
                                  ? "180deg"
                                  : "0deg"}
          );
  transition: transform 0.18s ease;
`;

const HeaderTitleArea = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
`;

const HeaderAccent = styled.span`
  width: 4px;
  height: 34px;
  flex: 0 0 4px;
  border-radius: 2px;
  background-color: #f5811f;
`;

const HeaderTitle = styled.div`
  margin-bottom: 4px;
  color: #222;
  font-size: var(--font-size-17);
  font-weight: 700;
`;

const HeaderDescription = styled.div`
  color: #888;
  font-size: var(--font-size-12);
  line-height: 1.5;
`;

const ConditionBody = styled.div`
  padding: 18px 20px 20px;
  animation: condition-dropdown-open 0.18s ease-out;

  @keyframes condition-dropdown-open {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 700px) {
    padding: 15px;
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns:
    minmax(170px, 0.7fr)
    minmax(390px, 1.5fr)
    minmax(250px, 0.9fr)
    74px;
  align-items: end;
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FilterItem = styled.div`
  min-width: 0;
`;

const DeviceFilterItem = styled.div`
  min-width: 0;

  @media (max-width: 1100px) {
    grid-column: 1 / -1;
  }

  @media (max-width: 720px) {
    grid-column: auto;
  }
`;

const FilterLabel = styled.div`
  margin-bottom: 8px;
  color: #555;
  font-size: var(--font-size-13);
  font-weight: 700;
`;

const FilterControl = styled.div`
  min-width: 0;
`;

const DeviceOptions = styled.div`
  display: flex;
  min-height: 40px;
  align-items: center;
  gap: 5px;
  padding: 5px 7px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
  flex-wrap: wrap;
`;

const DeviceOption = styled.div`
  display: flex;
  min-height: 28px;
  align-items: center;
  padding: 0 7px;
  border-radius: 5px;

  &:hover {
    background-color: #fffaf6;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin: 18px 0;
  background-color: #eeeeee;
`;

const PeriodSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PeriodTopRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;

  ${FilterLabel} {
    min-width: 72px;
    margin-bottom: 12px;
  }

  @media (max-width: 650px) {
    align-items: stretch;
    flex-direction: column;
    gap: 7px;

    ${FilterLabel} {
      margin-bottom: 0;
    }
  }
`;

const DatePickerWrap = styled.div`
  position: relative;
  width: 100%;

  @media (max-width: 650px) {
    width: 100%;
  }
`;

const CalendarIconWrap = styled.span`
  position: absolute;
  top: 50%;
  left: 12px;
  z-index: 2;
  display: inline-flex;
  color: #999;
  pointer-events: none;
  transform: translateY(-50%);
`;

const StyledDatePicker = styled(
    CustomDatePicker
)`
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px 0 37px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
  color: #444;
  font-size: var(--font-size-13);
  outline: none;

  &:hover {
    border-color: #f1d4c2;
  }

  &:focus {
    border-color: #f5811f;
  }
`;

const QuickDateList = styled.div`
  display: flex;
  gap: 6px;
  padding-left: 88px;
  flex-wrap: wrap;

  @media (max-width: 650px) {
    padding-left: 0;
  }
`;

const QuickDateButton = styled.button`
  min-height: 29px;
  padding: 0 10px;
  border: 1px solid
  ${({$active}) =>
          $active
                  ? "#f5811f"
                  : "#e5e5e5"};
  border-radius: 15px;
  background-color:
          ${({$active}) =>
                  $active
                          ? "#fff4e8"
                          : "#fff"};
  color:
          ${({$active}) =>
                  $active
                          ? "#f5811f"
                          : "#777"};
  font-size: var(--font-size-11);
  font-weight:
          ${({$active}) =>
                  $active
                          ? 700
                          : 500};
  cursor: pointer;

  &:hover {
    border-color: #f1d4c2;
    background-color: #fffaf6;
  }
`;

const SearchSection = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;

  @media (max-width: 650px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const SearchField = styled.div`
  width: 100%;
  max-width: 520px;
`;

const SearchInputWrap = styled.div`
  position: relative;
  width: 100%;
`;

const SearchIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 12px;
  display: inline-flex;
  color: #999;
  pointer-events: none;
  transform: translateY(-50%);
`;

const KeywordInput = styled.input`
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 39px 0 37px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
  color: #444;
  font-size: var(--font-size-13);
  outline: none;

  &:hover {
    border-color: #f1d4c2;
  }

  &:focus {
    border-color: #f5811f;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: #f1f1f1;
  cursor: pointer;
  transform: translateY(-50%);

  &::before,
  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 9px;
    height: 1px;
    background-color: #888;
    content: "";
  }

  &::before {
    transform:
            translate(-50%, -50%)
            rotate(45deg);
  }

  &::after {
    transform:
            translate(-50%, -50%)
            rotate(-45deg);
  }

  &:hover {
    background-color: #fff4e8;
  }

  &:hover::before,
  &:hover::after {
    background-color: #f5811f;
  }

  & > span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
`;

const ApplyButton = styled.button`
  min-width: 82px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-13);
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background-color: #ffe8d2;
  }

  @media (max-width: 650px) {
    width: 100%;
  }
`;

const PeriodFilterItem = styled.div`
  min-width: 0;
`;
